import { useCallback, useRef } from 'react'

import { Attachment } from 'features/attachments/types'
import { useAttachmentsAttributeDisplayState } from 'features/views/attributes/hooks/useAttachmentsAttributeDisplayState'
import { AttachmentValue } from 'features/views/attributes/types'

import { FileUploadModal } from 'ui/components/FileUpload'
import { useGlobalModal } from 'ui/components/Modal/useGlobalModal'

type UseSectionAttachmentAttributeEditorStateProps = {
    value?: AttachmentValue[]
    onChange: (value: AttachmentValue[]) => void
}

export function useSectionAttachmentAttributeEditorState({
    value,
    onChange,
}: UseSectionAttachmentAttributeEditorStateProps) {
    const effectiveValue = value ?? []
    const effectiveValueRef = useRef(effectiveValue)
    effectiveValueRef.current = effectiveValue

    const { show } = useGlobalModal(FileUploadModal)
    const onUploadClick = useCallback(() => {
        show({
            zIndex: 9999,
            onUploadSuccess: (files) => {
                const newAttachments: Attachment[] = files.map((file) => ({
                    filename: file.name,
                    mimetype: file.type,
                    size: file.size,
                    url: file.url,
                }))

                const prevValue = effectiveValueRef.current
                const newValue = [...prevValue, ...newAttachments]

                effectiveValueRef.current = newValue
                onChange(newValue)
            },
        })
    }, [show, onChange])

    const onRemoveAttachment = useCallback(
        (url: string) => {
            const prevValue = effectiveValueRef.current
            const newValue = prevValue.filter((value) => {
                if (typeof value === 'string') {
                    return value !== url
                }

                return value.url !== url
            })

            effectiveValueRef.current = newValue
            onChange(newValue)
        },
        [onChange]
    )

    const { attachments, isLoading } = useAttachmentsAttributeDisplayState({
        value,
        loadThumbnails: false,
    })

    return {
        localValue: attachments,
        onRemoveAttachment,
        onUploadClick,
        isLoading,
    }
}
